package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.SectionStatus
import co.maytes.standupbot.dashboard.components.widgets.SlackMessagePreview
import co.maytes.standupbot.dashboard.components.widgets.StandupUpdate
import co.maytes.standupbot.shared.dto.standuprun.StandupRunDetailDto
import co.maytes.standupbot.shared.dto.standuprun.UserSectionDto
import co.maytes.standupbot.shared.dto.standuprun.UserSectionStatusDto
import co.maytes.standupbot.shared.leave.LeaveReturnLabel
import com.varabyte.kobweb.compose.ui.Modifier
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@Composable
fun SlackPreviewPanel(
    detail: StandupRunDetailDto,
    currentUserId: String,
    onAck: () -> Unit,
    onEdit: () -> Unit,
    onSkip: () -> Unit,
    onRevert: () -> Unit,
    modifier: Modifier = Modifier,
    timezoneId: String? = null,
) {
    val zone = timezoneId?.let { runCatching { TimeZone.of(it) }.getOrNull() }
        ?: TimeZone.currentSystemDefault()
    val updates = detail.sections
        .filter { it.status != UserSectionStatusDto.OPTED_OUT && it.status != UserSectionStatusDto.ON_LEAVE }
        .map { it.toStandupUpdate() }
    val postDate = detail.scheduledFor.toLocalDateTime(zone).date
    val untilByUser = detail.leave.mapNotNull { row -> row.userId?.let { it to row.untilDate } }.toMap()
    val onLeave = detail.sections
        .filter { it.status == UserSectionStatusDto.ON_LEAVE }
        .map { section ->
            val name = section.displayName.takeIf { it.isNotBlank() } ?: section.slackHandle ?: "unknown"
            untilByUser[section.userId]?.let { "$name (until ${LeaveReturnLabel.of(it, postDate)})" } ?: name
        }

    val viewerHasSection = detail.sections.any { it.userId == currentUserId }
    val viewerSection = detail.sections.firstOrNull { it.userId == currentUserId }
    val canRevert = viewerSection != null && viewerSection.hasAiSnapshot &&
        (viewerSection.status == UserSectionStatusDto.EDITED || viewerSection.status == UserSectionStatusDto.SKIPPED_TODAY)

    SlackMessagePreview(
        channel = detail.standupName,
        timestamp = detail.postedAt?.let { formatTimeShort(it, zone) }
            ?: detail.generationCompletedAt?.let { formatTimeShort(it, zone) }
            ?: "",
        dateLabel = formatDateLong(detail.scheduledFor, zone),
        updates = updates,
        layout = detail.templateSections,
        onLeave = onLeave,
        onAck = onAck.takeIf { viewerHasSection },
        onEdit = onEdit.takeIf { viewerHasSection },
        onSkip = onSkip.takeIf { viewerHasSection },
        onRevert = onRevert.takeIf { canRevert },
        modifier = modifier,
    )
}

private fun UserSectionDto.toStandupUpdate(): StandupUpdate {
    val person = displayName.takeIf { it.isNotBlank() } ?: slackHandle ?: "unknown"
    val ackBadge = acknowledgedAt?.let { "approved ${formatRelative(it)}" }
    val sectionStatus = when (status) {
        UserSectionStatusDto.GENERATED -> SectionStatus.Generated
        UserSectionStatusDto.EDITED -> SectionStatus.Edited
        UserSectionStatusDto.EMPTY -> SectionStatus.Generated
        UserSectionStatusDto.OPTED_OUT -> SectionStatus.Generated
        UserSectionStatusDto.ON_LEAVE -> SectionStatus.Generated
        UserSectionStatusDto.SKIPPED_TODAY -> SectionStatus.SkippedToday
    }
    return StandupUpdate(
        person = person,
        yesterday = yesterday,
        today = today,
        blockers = blockers.takeIf { it.isNotBlank() },
        status = sectionStatus,
        acknowledgedBadge = ackBadge,
        userId = userId,
    )
}

private fun formatTimeShort(instant: Instant, zone: TimeZone): String {
    val dt = instant.toLocalDateTime(zone)
    val hour = dt.hour
    val minute = dt.minute
    val am = hour < 12
    val h12 = (hour % 12).let { if (it == 0) 12 else it }
    val mm = minute.toString().padStart(2, '0')
    return "$h12:$mm ${if (am) "AM" else "PM"}"
}

private fun formatDateLong(instant: Instant, zone: TimeZone): String {
    val dt = instant.toLocalDateTime(zone)
    val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[dt.monthNumber - 1]
    val dow = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")[dt.date.dayOfWeek.ordinal]
    return "$dow, $month ${dt.dayOfMonth}"
}
